<script setup>
import { WdsBreadCrumbs } from '@wds/components';
import { SEGMENT_PAGE_DATA } from '@/config/analytics';
import { APPDOWNLOADBANNERLINKS } from '@/config/app-download-banner-links.js';

// ### MIDDLEWARE TO VALIDATE ROUTE
definePageMeta({ middleware: 'validate-region' });

const { $tracking } = useNuxtApp();

const {
  t,
  locale,
} = useI18n();
const { useUrl } = useUrls();
const { isExtraSmallScreen } = useMq();
const route = useRoute();
const { buildSeo } = generateSeo();
const { useRouteManager } = useRoutes();
const { useImage } = useImages();
const { useFormatter } = useFormatters();

// ### COMPOSABLES - APIs
const { getRegionLocationTranslatedById } = useApiTranslator();
const { getRegionPageTags } = useApiSeo();
const { getTopPropertiesInLocation } = useApiTops();

// ### STORES
const sessionStore = useSessionStore();
const locationStore = useLocationStore();
const languageStore = useLanguageStore();
const sustainabilityStore = useSustainabilityStore();

// ### VARIABLES INITIALIZATION
const urlParams = route?.params || null;
const urlQuery = route?.query || null;
const pageInfoData = {
  numberOfProperties: 0,
  numberOfCities: 0,
  canonicalUrl: '',
  ogImage: '',
  avgRating: '',
  numberOfReviews: '',
  numberOfCountries: 0,
};

const locationKey = 'region';
const entryPageName = 'REGION';
const gtmPageName = 'regionLanding';
const gtmClassification = 'region';
const segmentTracking = SEGMENT_PAGE_DATA.region;

// ### ROUTE VALIDATION
const propertyTypeObj = useRouteManager.validatePropertyTypeParam(
  urlParams?.propertyType,
);
const propertyTypeKey = propertyTypeObj?.key;

const regionObj = await useRouteManager.validateRegionParam(
  propertyTypeObj,
  urlParams.country,
  urlParams.region,
);
const urlData = useRouteManager.getUrlData(
  urlParams.propertyType,
  urlParams?.continent,
  urlParams?.country,
  urlParams?.region,
);
useRouteManager.redirectRoute(urlData, urlQuery);

// ### API
const topPropertiesInLocation = await getTopPropertiesInLocation(
  propertyTypeKey,
  locationKey,
  regionObj?.id,
);

const seoData = await getRegionPageTags(propertyTypeKey, regionObj?.id);
const urlTranslations = await getRegionLocationTranslatedById(
  regionObj?.continentId,
  regionObj?.countryId,
  regionObj?.id,
);

const hrefUrls = useUrl.generateHrefLangForRegion(
  urlTranslations,
  propertyTypeKey,
);

if (regionObj && regionObj?.id) {
  pageInfoData.numberOfProperties = regionObj?.numberOfProperties;
  pageInfoData.numberOfCities = regionObj?.numberOfCities;
  pageInfoData.avgRating = regionObj?.avgRating;
  pageInfoData.numberOfReviews = regionObj?.numberOfReviews;
  pageInfoData.numberOfCountries = regionObj?.countries?.length;
  pageInfoData.latitude = regionObj?.latitude;
  pageInfoData.longitude = regionObj?.longitude;

  const imagesObj = {
    mobile: regionObj?.image?.mobile,
    tablet: regionObj?.image?.tablet,
    desktop: regionObj?.image?.desktop,
    heroImage: regionObj?.image?.heroImage,
  };

  locationStore.setLocation(locationKey, {
    id: regionObj?.id,
    name: regionObj?.name,
    image: imagesObj,
    slug: regionObj?.urlFriendlyName,
  });
  locationStore.setLocation('country', {
    id: regionObj?.countryId,
    name: regionObj?.country,
    image: imagesObj,
    slug: regionObj?.urlFriendlyCountry,
  });

  pageInfoData.canonicalUrl = useUrl.getRegionPageUrl({
    propertyTypeSlug: propertyTypeObj?.slug,
    urlFriendlyContinent: regionObj?.urlFriendlyContinent,
    urlFriendlyCountry: regionObj?.urlFriendlyCountry,
    urlFriendlyRegion: regionObj?.urlFriendlyName,
  });

  pageInfoData.ogImage = useImage.getImage(imagesObj?.desktop);
}

const crumbs = [
  {
    label: t('t_A11YBCTITLEPROPERTYTYPE', { PROPERTYTYPE: t(propertyTypeObj?.transcode) }),
    title: t(propertyTypeObj.transcode),
    link: useUrl.getWorldPageUrl({ propertyTypeSlug: propertyTypeObj?.slug }),
  },
  {
    label: t('t_A11YBCTITLELOCATION', {
      LOCATIONNAME: regionObj.continent,
      PROPERTYTYPE: t(propertyTypeObj?.transcode),
    }),
    title: t('t_A11YBCTEXTLOCATION', {
      LOCATIONNAME: regionObj.continent,
      PROPERTYTYPE: t(propertyTypeObj?.transcode),
    }),
    link: useUrl.getContinentPageUrl({
      propertyTypeSlug: propertyTypeObj?.slug,
      urlFriendlyContinent: regionObj?.urlFriendlyContinent,
    }),
  },
  {
    label: t('t_A11YBCTITLELOCATION', {
      LOCATIONNAME: regionObj.country,
      PROPERTYTYPE: t(propertyTypeObj?.transcode),
    }),
    title: t('t_A11YBCTEXTLOCATION', {
      LOCATIONNAME: regionObj.country,
      PROPERTYTYPE: t(propertyTypeObj?.transcode),
    }),
    link: useUrl.getCountryPageUrl({
      propertyTypeSlug: propertyTypeObj?.slug,
      urlFriendlyContinent: regionObj?.urlFriendlyContinent,
      urlFriendlyCountry: regionObj?.urlFriendlyCountry,
    }),
  },
  {
    label: t('t_A11YBCTITLELOCATION', {
      LOCATIONNAME: regionObj.name,
      PROPERTYTYPE: t(propertyTypeObj?.transcode),
    }),
    title: regionObj.name,
    link: '#',
  },
];

// ### SEO
const tradReplacements = {
  STATENAME: regionObj?.name,
  REGIONNAME: regionObj?.name,
  PROPCOUNT: regionObj?.numberOfProperties,
  CHOSENCONTINENT: regionObj?.name,
  CHOSENCOUNTRY: regionObj?.name,
  COUNTRY: regionObj?.country,
  LOCATIONNAME: regionObj?.name,
  PROPERTYTYPE: t(propertyTypeObj?.transcode_s),
  PROPERTYTYPES: t(propertyTypeObj?.transcode),
  LCPROPERTYTYPES: t(propertyTypeObj?.transcode),
  YEAR: new Date().getFullYear(),
  REVIEWSCOUNT: regionObj?.numberOfReviews,
  REVIEWCOUNT: regionObj?.numberOfReviews,
  NUMOFPROPERTIES: regionObj?.numberOfProperties,
  NUMOFCOUNTRIES: regionObj?.numberOfCountries,
  NUMOFCITIES: regionObj?.numberOfCities,
  ACTIVEFILTER: '',
};
const seoMetaData = buildSeo.getMetaData(
  seoData,
  pageInfoData,
  tradReplacements,
  false,
  locationKey,
);

const schemaCrumbs = crumbs?.map((item) => {
  return {
    name: item?.title,
    item: item?.link,
  };
});

useSchemaOrg([
  defineBreadcrumb({ itemListElement: schemaCrumbs }),
  {
    '@type': 'Place',
    'name': regionObj.name,
    'description': seoMetaData?.description,
  },
]);

// ### COMPUTED
const getLocationsList = computed(() => {
  if (!regionObj?.cities) {
    return [];
  }

  return regionObj?.cities?.map((location) => {
    return {
      url: useUrl.getCityPageUrl({
        propertyTypeSlug: propertyTypeObj?.slug,
        urlFriendlyContinent: location?.urlFriendlyContinent,
        urlFriendlyCountry: location?.urlFriendlyCountry,
        urlFriendlyCity: location?.urlFriendlyName,
      }),
      title: location.name,
      aria: location.name,
      name: location.name,
    };
  });
});

const getMainTitle = computed(() => {
  if (seoMetaData?.h1) {
    return seoMetaData.h1;
  }

  return t('t_PROPERTYTYPEINLOCATION', {
    PROPERTYTYPE: t(propertyTypeObj?.transcode),
    LOCATION: `${regionObj.name}`,
  });
});

const getPropertiesCountText = computed(() => {
  let translation = t('t_RWDWEHAVEXHOSTELSINLOCATIONSHORT', {
    NUMOFPROPERTIES: regionObj.numberOfProperties,
    NUMOFCITIES: regionObj.numberOfCities,
    PROPERTYTYPE:
      regionObj.numberOfProperties === 1
        ? t(propertyTypeObj.transcode_s)
        : t(propertyTypeObj.transcode),
    CITYORCITIES: regionObj.numberOfCities === 1 ? t('t_CITY') : t('t_CITIES'),
    LOCATION: `${regionObj.name}`,
  });

  if (regionObj.avgRating > 0) {
    translation += ` ${t('t_RWDWEHAVEXHOSTELSINLOCATIONRATINGS', {
      AVGRATING: useFormatter.formatRating(regionObj.avgRating),
      NUMOFREVIEWS: regionObj.numberOfReviews,
    })}`;
  }
  return translation;
});

const getCrumbsShownByViewport = computed(() => {
  return toValue(isExtraSmallScreen) ? 2 : null;
});

const averageCityPriceList = computed(() => {
  return regionObj?.cities;
});

const getMapConfigs = computed(() => ({
  scrollZoom: false,
  center: [regionObj?.longitude, regionObj?.latitude],
  zoom: 9,
}));

const getMapMakersList = computed(() => {
  return regionObj?.cities
    .filter((city) => city?.totalPropertiesAvailable)
    .reduce((markers, city) => {
      markers.push({
        id: city.id,
        name: city.name,
        latitude: city.latitude,
        longitude: city.longitude,
        numProperties: city?.totalPropertiesAvailable || 0,
        url: useUrl.getCityPageUrl({
          propertyTypeSlug: propertyTypeObj?.slug,
          urlFriendlyContinent: city.urlFriendlyContinent,
          urlFriendlyCountry: city.urlFriendlyCountry,
          urlFriendlyCity: city.urlFriendlyName,
        }),
        citySummary: {
          numProperties: city.totalPropertiesAvailable,
          avgDormPrice: city.avgDormPrice,
          avgPrivatePrice: city.avgPrivatePrice,
        },
      });
      return markers;
    }, []);
});

const hasTopPropertiesInLocation = computed(() => {
  return topPropertiesInLocation && topPropertiesInLocation?.length > 0;
});

const getAppDownloadDetails = computed(() => {
  return {
    page: 'region',
    linkPaths: APPDOWNLOADBANNERLINKS?.REGION,
    linkParams: 'search',
  };
});

function handleSustainabilityPinClick(propertySustainabilityInfo) {
  sustainabilityStore.setSustainabilityDetails(propertySustainabilityInfo);

  const trackingInfo = {
    gtm: {
      parameter1: `SUSTAINABILITY_TIER_${propertySustainabilityInfo?.tier}`,
      parameter2: gtmPageName,
    },
  };

  $tracking.onSustainabilityIconClick(trackingInfo.gtm, trackingInfo.segment);
}

useHead(() => ({
  htmlAttrs: { lang: locale?.value },
  link: [
    {
      rel: 'canonical',
      href: seoMetaData?.canonicalUrl,
    },
  ].concat(hrefUrls),
}));

useSeoMeta({
  title: () => seoMetaData?.title,
  ogTitle: () => seoMetaData?.ogTitle,
  description: () => seoMetaData?.description,
  ogDescription: () => seoMetaData?.ogDescription,
  ogUrl: () => seoMetaData?.canonicalUrl,
  ogImage: () => seoMetaData?.ogImage,
  ogImageAlt: () => seoMetaData?.ogDescription,
  ogType: () => 'website',
  keywords: () => seoMetaData?.keywords,
  robots: () => 'index, follow, maxImagePreview:large',
  twitterCard: () => 'summary_large_image',
  twitterTitle: () => seoMetaData?.ogTitle,
  twitterDescription: () => seoMetaData?.ogDescription,
  twitterImage: () => seoMetaData?.ogImage,
  twitterImageAlt: () => seoMetaData?.ogDescription,
});

// ### TRACKING
sessionStore.setEntryPoint(entryPageName);

onMounted(() => {
  const trackingPropertyType = propertyTypeObj?.type?.toLowerCase();
  const trackingContinentName = regionObj?.tracking?.continent;
  const trackingCountryName = regionObj?.tracking?.country;
  const trackingRegionName = regionObj?.tracking?.name;

  const trackingInfo = {
    gtm: {
      gtmPropertyType: trackingPropertyType,
      gtmCanonicalUrl: seoMetaData?.canonicalUrl,
      gtmContinent: trackingContinentName,
      gtmCountry: trackingCountryName,
      gtmRegion: trackingRegionName,
      gtmPageName,
      gtmClassification,
    },
    segment: {
      event_name: segmentTracking.VIEWED,
      page_type: segmentTracking.TYPE,
      property_type: trackingPropertyType,
      destination_continent: trackingContinentName,
      destination_country: trackingCountryName,
      destination_region: trackingRegionName,
    },
  };

  $tracking?.trackOnPageLoad(trackingInfo.gtm, trackingInfo.segment);
});
</script>

<script>
/* eslint-disable */
import { ca } from "@/lib/channelAttribution.js";

const entryPageNameJs = "REGION";

if (import.meta.client) {
  ca?.setChannelAttribution(null, entryPageNameJs);
}
/* eslint-enable */
</script>

<template>
  <LazyCommonHwAppDownloadBanner v-bind="getAppDownloadDetails" />

  <CommonHwHeader
    :free-cancellation-text="$t('t_CANCELFORFREEDESCRIPTION')"
    :headline="getMainTitle"
    :sub-label="getPropertiesCountText"
  />

  <CommonHwLanguagePicker v-show="languageStore.isLanguagePickerOpen" />

  <WdsBreadCrumbs
    class="hw-wrapper breadcrumbs-section"
    :breadcrumbs="crumbs"
    :max-crumbs-shown="getCrumbsShownByViewport"
    :reversed="false"
  />

  <CommonHwFreeCancellationPropertyTypeContainer
    class="hw-wrapper cancellation-and-property-type-section"
    :active-property-type="propertyTypeObj.key"
  />

  <CommonHwPopularPropertiesInLocation
    v-if="hasTopPropertiesInLocation"
    class="hw-wrapper full-width regions-section"
    :property-type-obj="propertyTypeObj"
    :location-name="regionObj?.name"
    :location-list="topPropertiesInLocation"
    @sustainability-pin-click="handleSustainabilityPinClick"
  />

  <section class="hw-wrapper full-width regions-section map-section">
    <CommonHwMap
      class="map-wrapper"
      :map-config="getMapConfigs"
      :marker-list="getMapMakersList"
      :property-type-obj="propertyTypeObj"
    />
  </section>

  <CommonHwAverageCityPrice
    v-if="averageCityPriceList"
    class="hw-wrapper regions-section"
    :average-city-prices-list="averageCityPriceList"
    :property-type-obj="propertyTypeObj"
  />

  <CommonHwOtherLocationsInLocation
    v-if="getLocationsList"
    class="hw-wrapper regions-section"
    :location-name="regionObj?.name"
    :other-locations="getLocationsList"
    :property-type-obj="propertyTypeObj"
  />

  <CommonHwPageTextContent
    v-if="seoMetaData.copy"
    class="hw-wrapper regions-section regions-about-section"
    heading-level="h2"
    :title="
      $t('t_ABOUTLOCATION', { LOCATION: `<span>${regionObj?.name}</span>` })
    "
    :content="seoMetaData.copy"
  />
</template>

<style lang="scss" scoped>
.regions-section {
  margin: wds-rem(72px) auto;

  @include desktop {
    margin: wds-rem(120px) auto;
  }
}

.breadcrumbs-section {
  padding-left: $wds-spacing-s;

  @include tablet {
    padding-left: $wds-spacing-m;
  }

  @include desktop {
    padding-left: $wds-spacing-xl;
  }
}

.cancellation-and-property-type-section {
  padding: 0;

  @include desktop {
    padding: 0 wds-rem(40px);
  }
}

.map-section {
  .map-wrapper,
  :deep(.map-wrapper) {
    height: wds-rem(560px);
  }
}

.regions-about-section {
  :deep(.content) {
    p {
      @include body-1-reg;
    }

    a {
      @include body-1-bld;
    }
  }
}
</style>
